import type { DeviceCapabilities } from '../services/deviceCapabilities';
import type { EditorPreferences } from '../services/persistence/preferences';

interface Props {
  zoom: number;
  saveStatus: 'saved' | 'saving' | 'error';
  capabilities: DeviceCapabilities;
  nodeCount: number;
  edgeCount: number;
  pendingSyncCount: number;
  serverConnected: boolean;
  cloudSaved: boolean;
  cloudUpdatedAt: string | null;
  offline: boolean;
  realtimeConnected: boolean;
  collaboratorCount: number;
  remoteUpdateAvailable: boolean;
  readOnly?: boolean;
  preferences: EditorPreferences;
  onPreferences: (preferences: EditorPreferences) => void;
  onZoom: (zoom: number) => void;
  onReviewRemoteUpdate?: () => void;
  onCollaborators?: () => void;
  onSyncNow?: () => void;
  syncBusy?: boolean;
}

export function StatusBar({ zoom, saveStatus, capabilities, nodeCount, edgeCount, pendingSyncCount, serverConnected, cloudSaved, cloudUpdatedAt, offline, realtimeConnected, collaboratorCount, remoteUpdateAvailable, readOnly = false, preferences, onPreferences, onZoom, onReviewRemoteUpdate, onCollaborators, onSyncNow, syncBusy = false }: Props) {
  const saveLabel = saveStatus === 'saved' ? 'Salvo localmente' : saveStatus === 'error' ? 'Erro ao salvar localmente' : 'Salvando...';
  return (
    <footer className="statusbar" role="status" aria-live="polite">
      <div className="status-group">
        <span className={`save-dot ${saveStatus === 'saved' ? 'ok' : saveStatus === 'error' ? 'error' : ''}`} />
        <span>{saveLabel}</span>
        <span className="status-separator">•</span>
        <span>{nodeCount} objetos</span>
        <span>{edgeCount} conexões</span>
        {readOnly && <><span className="status-separator">•</span><strong className="status-readonly">Somente leitura</strong></>}
        <span className="status-separator">•</span>
        {serverConnected && !offline && !readOnly && onSyncNow ? <button type="button" className={`cloud-save-status cloud-save-action ${pendingSyncCount > 0 || syncBusy ? 'syncing' : cloudSaved ? 'synced' : 'local'}`} onClick={onSyncNow} disabled={syncBusy} title={pendingSyncCount > 0 ? 'Clique para tentar sincronizar agora com a OWDEX.' : cloudSaved ? `Projeto salvo nos servidores OWDEX${cloudUpdatedAt ? ` · ${new Date(cloudUpdatedAt).toLocaleString('pt-BR')}` : ''}. Clique para conferir pendências.` : 'Clique para salvar/sincronizar este projeto agora na OWDEX.'}>
          {syncBusy ? 'Sincronizando…' : pendingSyncCount > 0 ? `Sincronizar agora · ${pendingSyncCount}` : cloudSaved ? 'Salvo na OWDEX' : 'Salvar na OWDEX'}
        </button> : <span className={`cloud-save-status ${offline ? 'offline' : pendingSyncCount > 0 ? 'syncing' : cloudSaved ? 'synced' : 'local'}`} title={offline ? 'Sem conexão. O trabalho continua salvo neste dispositivo e será sincronizado quando a internet voltar.' : pendingSyncCount > 0 ? 'Há alterações locais aguardando envio para a OWDEX.' : cloudSaved ? `Projeto salvo nos servidores OWDEX${cloudUpdatedAt ? ` · ${new Date(cloudUpdatedAt).toLocaleString('pt-BR')}` : ''}` : 'Este projeto ainda existe apenas neste dispositivo.'}>
          {offline ? 'Trabalhando offline' : pendingSyncCount > 0 ? `Sincronizando… ${pendingSyncCount}` : cloudSaved ? 'Salvo na OWDEX' : 'Somente neste dispositivo'}
        </span>}
        {serverConnected && <><span className="status-separator">•</span><span title={realtimeConnected ? 'Canal de presença/eventos ativo.' : 'Servidor conectado, mas canal em tempo real indisponível no momento.'}>{realtimeConnected ? 'Tempo real' : 'Tempo real reconectando'} · <button type="button" className="status-link-button" onClick={onCollaborators}>{collaboratorCount} outro(s) online</button></span></>}
        {remoteUpdateAvailable && <><span className="status-separator">•</span><button type="button" className="status-warning status-warning-button" onClick={onReviewRemoteUpdate}>Revisar atualização remota</button></>}
      </div>
      <div className="status-group device-status" title={`OffscreenCanvas: ${capabilities.offscreenCanvas ? 'sim' : 'não'} • WebGPU: ${capabilities.webgpu ? 'sim' : 'não'}`}>
        <span>PC: {capabilities.tier}</span>
        <span>{capabilities.logicalProcessors} threads</span>
        <span>{capabilities.deviceMemoryGb ? `~${capabilities.deviceMemoryGb} GB RAM` : 'RAM gerenciada pelo navegador'}</span>
        <span>{capabilities.webgl2 ? 'WebGL2' : 'Canvas'}</span>
      </div>
      <div className="status-editor-options">
        <button type="button" className={preferences.gridVisible ? 'active' : ''} onClick={() => onPreferences({ ...preferences, gridVisible: !preferences.gridVisible })} title="Mostrar ou ocultar grade">Grade</button>
        <button type="button" className={preferences.snapToGrid ? 'active' : ''} onClick={() => onPreferences({ ...preferences, snapToGrid: !preferences.snapToGrid })} title="Encaixar objetos na grade">Snap</button>
        <select value={preferences.gridSize} onChange={(event) => onPreferences({ ...preferences, gridSize: Number(event.target.value) as EditorPreferences['gridSize'] })} aria-label="Tamanho da grade">
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
        </select>
      </div>
      <div className="zoom-control">
        <button type="button" onClick={() => onZoom(Math.max(0.2, zoom - 0.1))}>−</button>
        <span>{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => onZoom(Math.min(3, zoom + 0.1))}>+</button>
      </div>
    </footer>
  );
}
